/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import {classToClass} from "@qwoach/class-transformer-wrapper";
import BaseObject from "@shared/BaseObject";
import QwoachDataManager from "@/core/js/backend/QwoachDataManager";
import {SessionChangeTypeEnum} from "@shared/scheduler/SessionChangeTypeEnum";
import CalendarEventResponse from "@shared/scheduler/CalendarEventResponse";
import {GetCalendarEventRequest} from "@shared/scheduler/GetCalendarEventRequest";
import dayjs from 'dayjs'
import {newNumberedName} from "@/utils/helpers";
import {RecurringChangeType} from "@shared/scheduler/RecurringChangeType";
import {SessionConfirmationStatus, StepType} from "@shared/Program/ProgramStep/helpers/ProgramStepEnums";
import {ProgramSessionWrapper} from "@shared/scheduler/ProgramSessionWrapper";
import {RecurrenceRangeType} from "@shared/Recurrence/RecurrenceEnums";
import ScheduledEvent from "@shared/scheduler/ScheduledEvent";
import {SessionTimeOptionsEnum} from "@shared/Program/ProgramStep/helpers/SessionTimeOptions";
import SectionProgramStep from "@shared/Program/ProgramStep/SectionProgramStep";
import CoachingSessionStep from "@shared/Program/ProgramStep/CoachingSessionStep";


export class SessionsCalendarSyncManager extends BaseObject{

	dataManager: QwoachDataManager
	recurringEventsToShow = 4

	constructor(dataManager: QwoachDataManager) {
		super();
		this.dataManager = dataManager
	}

	//create
	//update
	//delete
	async syncSession(data){
		let {
			sessionStep,
			stepOriginal = new CoachingSessionStep(),
			sessionItem,
			sessionType,
			programItem,
			clientProfile,
			changeType,
			shouldNotifyClient
		} = data
		let isRecurring = sessionStep.isRecurring
		console.log("syncSession start: isRecurring = ", isRecurring, ", shouldNotifyClient = ", shouldNotifyClient)
		console.log("syncSession start: data = ", classToClass(data))

		if(isRecurring){
			await this.syncRecurringSession(sessionStep, stepOriginal, sessionItem, sessionType, programItem, clientProfile, changeType, shouldNotifyClient)
		} else {
			await this.syncOneTimeSession(sessionStep, stepOriginal, sessionItem, sessionType, programItem, clientProfile, changeType, shouldNotifyClient)
		}

		return programItem.findStepById(sessionStep.id)
	}


	async deleteSession(data){
		let {
			sessionStep,
			sessionItem,
			sessionType,
			programItem,
			changeType,
			shouldNotifyClient = true
		} = data

		let baseWrapper = new ProgramSessionWrapper(sessionStep, sessionItem, sessionType)

		this.logWrapper(baseWrapper, "deleteSession: ")
		console.log("deleteSession: shouldNotifyClient = ", shouldNotifyClient)

		if(sessionStep.isRecurring){
			await this.deleteRecurringSession(baseWrapper, programItem, changeType, shouldNotifyClient)
		} else {
			await this.deleteOneTimeSession(baseWrapper, programItem, shouldNotifyClient)
		}
	}


	async syncOneTimeSession(step, stepOriginal, sessionItem, sessionType, programItem, clientProfile, changeType, shouldNotifyClient){
		console.log("syncOneTimeSession start")

		let isRecurringPrev = stepOriginal.isRecurring

		let baseWrapper = new ProgramSessionWrapper(step, sessionItem, sessionType)
		baseWrapper = classToClass(baseWrapper)//deep clone to ensure changing baseWrapper doesn't change any items in the programItem
		baseWrapper.programStepOriginal = stepOriginal
		baseWrapper.saveFormerSession()

		console.log("syncOneTimeSession: isRecurringPrev = ", isRecurringPrev)

		if(isRecurringPrev){
			// if(changeType === RecurringChangeType.ONLY_THIS){
			// 	await this.dataManager.apiPostEventSyncDelete(sessionItem)
			// } else {
				await this.deleteRecurringSessionThisAndFuture(baseWrapper, programItem, true)
			// }
			sessionItem = null
		}

		// if(!sessionItem && step.timeOptions === SessionTimeOptionsEnum.SPECIFIC_TIME){
		// 	return await this.createOneTimeSession(baseWrapper, programItem, clientProfile, shouldNotifyClient)
		// } else {
		// 	return await this.updateOneTimeSession(baseWrapper, programItem, shouldNotifyClient)
		// }
		if(sessionItem){
			return await this.updateOneTimeSession(baseWrapper, programItem, shouldNotifyClient)
		} else {
			return await this.createOneTimeSession(baseWrapper, programItem, clientProfile, shouldNotifyClient)
		}

	}

	async createOneTimeSession(baseWrapper, programItem, clientProfile, shouldNotifyClient){
		console.log("createOneTimeSession")
		this.logWrapper(baseWrapper, "createOneTimeSession: ")
		let sessionItem = await this.createSessionItem(baseWrapper, programItem, clientProfile)
		let programStep = programItem.findStepById(baseWrapper.programStep.id)
		let wrapper = new ProgramSessionWrapper(programStep, sessionItem, baseWrapper.sessionType)
		wrapper.fillStepFromDiff(baseWrapper.programStep, baseWrapper.programStepOriginal)
		wrapper.fillSessionFromStep()
		wrapper.programStep.syncFromSessionItem(sessionItem)
	}

	async updateOneTimeSession(baseWrapper, programItem, shouldNotifyClient){
		console.log("updateOneTimeSession: shouldNotifyClient = ", shouldNotifyClient)
		this.logWrapper(baseWrapper, "updateOneTimeSession: ")
		// we need to get programStep from the programItem
		// to ensure we change the correct instance moving forward
		console.log("updateOneTimeSession: programItem = ", programItem)
		let programStep = programItem.findStepById(baseWrapper.programStep.id)
		console.log("updateOneTimeSession: findStepById: baseWrapper.programStep.id = ", baseWrapper.programStep.id, ", programStep = ", programStep)
		let sessionWrapperSeries = await this.loadWrappersForSteps([programStep], baseWrapper.sessionType)
		console.log("updateOneTimeSession: sessionWrapperSeries = ", sessionWrapperSeries)

		let sessionWrapper = sessionWrapperSeries[0]

		sessionWrapper.saveFormerSession()
		sessionWrapper.fillStepFromDiff(baseWrapper.programStep, baseWrapper.programStepOriginal)
		sessionWrapper.fillSessionFromStep()

		this.logWrapper(classToClass(sessionWrapper), "updateOneTimeSession: ")

		let updateEventPromise = Promise.resolve()
		if(sessionWrapper.sessionItem){
			let sessionItem = classToClass(sessionWrapper.sessionItem);
			sessionItem.notifyClient = shouldNotifyClient
			updateEventPromise = this.dataManager.apiPostCalendarEventUpdate(sessionItem)
		}
		await Promise.all([
			updateEventPromise,
			this.updateWrappers([sessionWrapper])
		])
	}

	async deleteOneTimeSession(baseWrapper, programItem, shouldNotifyClient = false){
		console.log("deleteOneTimeSession")
		this.logWrapper(baseWrapper, "deleteOneTimeSession: ")
		let sessionItem = classToClass(baseWrapper.sessionItem)
		if(sessionItem){
			sessionItem.notifyClient = shouldNotifyClient
		}
		await Promise.all([
			sessionItem ? this.dataManager.apiPostCalendarEventDelete(sessionItem) : Promise.resolve(),
			this.deleteWrappers([baseWrapper], programItem)
		])
	}

	async syncRecurringSession(step, stepOriginal = new CoachingSessionStep(), sessionItem, sessionType, programItem, clientProfile, changeType, shouldNotifyClient){
		console.log("syncRecurringSession start")
		console.log("syncRecurringSession programItem = ", classToClass(programItem))
		console.log("syncRecurringSession step.startTime = ", step.confirmedTime, ", stepOriginal.startTime = ", stepOriginal.confirmedTime, ", sessionItem.startTime = ", sessionItem ? sessionItem.startTime : null)
		let needSaveParentItem = false

		let isRecurring = step.isRecurring

		console.log("syncRecurringSession: isRecurring = ", isRecurring, ", sessionItem = ", sessionItem)
		if(!isRecurring && !sessionItem){
			console.log("syncRecurringSession: no need to sync. skip")
			return false
		}


		if(sessionItem && !sessionItem.isRecurring()){
			console.log("syncRecurringSession: cancelling sessionItem: ", sessionItem)
			sessionItem.changeType = SessionChangeTypeEnum.CANCEL
			sessionItem.reasonForChange = "Changed to recurring session"
			await this.dataManager.apiPostEventSyncDelete(sessionItem)
			sessionItem = null
		}


		let baseWrapper = new ProgramSessionWrapper(step, sessionItem, sessionType)
		baseWrapper = classToClass(baseWrapper)//deep clone to ensure changing baseWrapper doesn't change any items in the programItem
		baseWrapper.programStepOriginal = stepOriginal
		baseWrapper.saveFormerSession()
		baseWrapper.fillSessionFromStep()

		console.log("baseWrapperFull = ", baseWrapper)
		this.logWrapper(baseWrapper, "debug1: start baseWrapper: ")

		if(isRecurring && !sessionItem){
			needSaveParentItem = await this.createRecurringSession(baseWrapper, programItem, clientProfile, shouldNotifyClient)
		} else if(!isRecurring && sessionItem){
			needSaveParentItem = await this.deleteRecurringSession(baseWrapper, programItem, changeType, shouldNotifyClient)
		} else if(isRecurring && sessionItem){
			needSaveParentItem = await this.updateRecurringSession(baseWrapper, programItem, changeType, clientProfile, shouldNotifyClient)
		}
		console.log("syncRecurringSession end")

		// await this.testDebug(programItem, baseWrapper.sessionType)

		return needSaveParentItem
	}

	async testDebug(programItem, sessionType){
		console.warn("WARNIGN!!! this is test debug - remove this method from production!!! ==============================")
		let steps = programItem.findStepAll(step => step.isTypeCall())
		let wrapperSeries = await this.loadWrappersForSteps(steps, sessionType)
		this.logWrapperArray(wrapperSeries)
	}

	logWrapperArray(programWrapperSeries, msg){
		for(let wrapper of programWrapperSeries){
			this.logWrapper(wrapper, msg)
		}
	}
	logWrapper(programWrapper, msg = "", keys, excludeKeys){
		let programStep = programWrapper.programStep
		let sessionItem = programWrapper.sessionItem
		let sessionType = programWrapper.sessionType
		keys = keys || [
			"rec",
			"time",
			"id",
			"formerEvent",
			"emailReminder"
		]
		excludeKeys = excludeKeys || [
			"owner",
			"timeBasedItemId",
			"timeOptions",
			"timeSource",
			"copiedFromId",
			"timeSlots",
			"suggestedDateTimeTs",
			"startTimeFormer",
			"notifyClient"
		]
		let obj = {
			programStep: this.fetchValuesByKeyPattern(programStep, keys, excludeKeys),
			sessionItem: this.fetchValuesByKeyPattern(sessionItem, keys, excludeKeys),
			sessionType: this.fetchValuesByKeyPattern(sessionType, keys, excludeKeys),
		}
		console.log(msg + " programWrapper = ", obj)//, ", full wrapper = ", programWrapper)
	}

	fetchValuesByKeyPattern(obj, keyPatterns, excludeKeys){
		let res = {}
		if(obj){
			Object.keys(obj).forEach(key => {
				if(this.canShowKey(key, keyPatterns, excludeKeys)){
					let val = obj[key]
					if(key.toLowerCase().indexOf("time") >= 0){
						val = dayjs(val).format("llll")
					}
					res[key] = val

					if(key == "recurrenceIndex" && val < 1){
						console.error("ha-ha-ha!! got you!! recurrenceIndex is incorrect here")
					}
				}
			})
		}
		return res
	}

	canShowKey(key, keyPatterns, excludeKeys){
		key = key.toLowerCase()
		for(let patterns of keyPatterns){
			if(key.indexOf(patterns.toLowerCase()) >= 0){
				for(let excluded of excludeKeys){
					if(key.indexOf(excluded.toLowerCase()) >= 0){
						return false
					}
				}
				return true
			}
		}
		return false

	}

	async createRecurringSession(baseWrapper, programItem, clientProfile, shouldNotifyClient){
		console.log("syncRecurringSession.createRecurringSession: start ")

		let baseSessionItem = await this.createSessionItem(baseWrapper, programItem, clientProfile, shouldNotifyClient)
		baseSessionItem.recurrenceIndex = 1
		baseSessionItem.startTimeRecurringMaster = baseSessionItem.startTime

		let eventInstances = await this.getEventInstances(baseSessionItem)
		let firstInstance = eventInstances.recurringInstances[0]

		baseWrapper.sessionItem = baseSessionItem

		this.fillWrapperFromCalendarEvent(baseWrapper, baseWrapper, firstInstance, programItem)

		// we need to get programStep from the programItem
		// to ensure we change the correct instance moving forward
		let programStep = programItem.findStepById(baseWrapper.programStep.id)
		let wrapperSeries = [new ProgramSessionWrapper(programStep, baseSessionItem, baseWrapper.sessionType)]


		await this.syncFutureRecurringEvents(baseWrapper, wrapperSeries, programItem, eventInstances.recurringInstances)

		return true

	}

	async createSessionItem(baseWrapper, programItem, clientProfile, shouldNotifyClient){
		let {programStep, sessionType} = baseWrapper

		let sessionItem = new ScheduledEvent()
		sessionItem.id = this.dataManager.generateId();
		sessionItem.fillFromSessionType(sessionType, clientProfile)
		sessionItem.fillAnswersFromCallStep(programStep)
		// sessionItem.participants.forEach(participant => participant.confirmation = ConfirmationEnum.CONFIRMED)

		if(sessionItem.recurrence){
			sessionItem.isMasterEvent = true
		}

		sessionItem.programId = programItem.id
		sessionItem.stepId = programStep.id
		sessionItem.addOwner([programStep.id, programItem.id, sessionType.coachId])
		sessionItem.notifyClient = shouldNotifyClient

		return await this.dataManager.apiPostEventCreate(sessionItem)
	}

	fillStepFromEventInstance(step, eventInstance){
		step.recurMasterId = eventInstance.getQwoachMasterEventId()
		step.syncFromSessionItem(eventInstance)
	}

	async showNewRecurringEventsIfNeeded(programItem, sessionType) {
		if(!sessionType){
			return
		}
		console.log("showNewRecurringEventIfNeeded: programItem = ", programItem)
		let programStep = programItem.findLastRecurringSession()
		if (!programStep || !programStep.hasMoreRecurringEvents) {
			console.log("showNewRecurringEventIfNeeded: skipping. programStep = ", programStep)
			return false
		}

		let wrappers = await this.loadWrappersForSteps([programStep], sessionType)
		let baseWrapper = wrappers[0]
		this.logWrapper(baseWrapper, "showNewRecurringEventIfNeeded: wrapper = ")

		if (!baseWrapper.sessionItem) {
			//this shouldn't happen
			console.error("showNewRecurringEventIfNeeded: skipping. sessionItem is null. baseWrapper = ", baseWrapper)
			return
		}

		let recurringEventsToShow = await this.recurringEventsToShowFromNextRecurringSession(baseWrapper, programItem)

		// we first fetch all future programSteps without loading sessionItems
		// to see if all the steps are already shown to save database reads
		let optionsExamine = {
			startStepId: programStep.id,
			skipLoadingSessionType: true,
			skipLoadingSessionItem: true
		}
		let sessionWrapperSeriesExamine = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem, optionsExamine)
		if (sessionWrapperSeriesExamine.length >= recurringEventsToShow) {
			console.log("showNewRecurringEventIfNeeded: skipping. all future steps are shown")
			return
		}
		// TODO potential bug: if there is a gap between sessionItem.startTime and new Date().getTime()
		// we need to fetch all the instances between those timestamps
		// to correctly adjust the recurrenceIndex
		// it should be something like this:
		//
		// let recurrenceIndexAdjust = 0
		// if(baseWrapper.sessionItem.startTime < new Date().getTime()){
		// 	let response = await this.getEventInstances(baseWrapper.sessionItem, 10000, baseWrapper.sessionItem.startTime + 1, new Date().getTime())
		// 	recurrenceIndexAdjust = response.recurringInstances.length
		// }
		// baseWrapper.sessionItem.recurrenceIndex += recurrenceIndexAdjust
		//
		// but this needs proper testing first

		let startTime = Math.max(baseWrapper.sessionItem.startTime, new Date().getTime())
		let options = {
			startStepId: programStep.id,
			skipLoadingSessionType: true,
			skipLoadingSessionItem: false,
			startTime: startTime
		}
		let sessionWrapperSeries = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem, options)
		let masterEvent = baseWrapper.sessionItem.createRecurringMasterEvent()
		masterEvent.startTime = startTime
		let response = await this.getEventInstances(masterEvent, recurringEventsToShow)
		await this.syncFutureRecurringEvents(baseWrapper, sessionWrapperSeries, programItem, response.recurringInstances)

		let hasMoreSessions = response.recurringInstances.length > 0 && response.recurringInstances[0].hasMoreRecurringEvents
		console.log("showNewRecurringEventIfNeeded: hasMoreSessions = ", hasMoreSessions)
		if(!hasMoreSessions){
			await this.syncPreviousEvents(baseWrapper, programItem, null, null, true)
		}
		return true

	}

	async syncFutureRecurringEvents(baseWrapper, sessionWrapperSequence, programItem, eventInstances){
		console.log("syncFutureRecurringEvents: baseWrapper = ", baseWrapper, ", sessionWrapperSequence = ", sessionWrapperSequence)
		let wrappersToCreate = []
		let wrappersToUpdate = []
		let wrappersToDelete = []

		// we need to ensure that baseWrapper differs from
		// any wrappers in sessionWrapperSequence
		// so we can ensure it stays intact while we are updating other sessions
		baseWrapper = classToClass(baseWrapper)


		let eventInstancesLocal
		if(eventInstances){
			eventInstancesLocal = classToClass(eventInstances)
		} else {
			let response = await this.getEventInstances(baseWrapper.sessionItem)
			eventInstancesLocal = response.recurringInstances
		}

		console.log("debug: syncFutureRecurringEvents: eventInstancesLocal = ", classToClass(eventInstancesLocal))
		for(let wrapper of sessionWrapperSequence){
			let {sessionItem} = wrapper
			let calendarEventIndex = eventInstancesLocal.findIndex(calendarEvent => sessionItem.hasCalendarEventId(calendarEvent))
			console.log("debug: syncFutureRecurringEvents: calendarEventIndex = ", calendarEventIndex, ", sessionItem = ", classToClass(sessionItem), ", ")
			if(calendarEventIndex >= 0){
				let calendarEvent = eventInstancesLocal.splice(calendarEventIndex, 1)[0]
				// this.updateWrapperFromCalendarEventAndBaseWrapper(baseWrapper, wrapper, calendarEvent, programItem)
				this.logWrapper(wrapper, "debug1: update: before sync: ")
				wrapper.saveFormerSession()
				let hasChanged = wrapper.fillStepFromDiff(baseWrapper.programStep, baseWrapper.programStepOriginal)
				wrapper.fillSessionFromStep()
				this.fillWrapperFromCalendarEvent(baseWrapper, wrapper, calendarEvent, programItem)
				this.logWrapper(wrapper, "debug1: update: after sync: ")
				if(hasChanged){
					wrappersToUpdate.push(wrapper)
				}
			} else {
				wrappersToDelete.push(wrapper)
			}
		}

		for(let i = 0; i < eventInstancesLocal.length; ++i){
			let calendarEvent = eventInstancesLocal[i]
			let wrapper = this.createNewSectionWithSessionFromCalendarEvent(baseWrapper, calendarEvent, programItem)
			wrappersToCreate.push(wrapper)
		}

		await Promise.all([
			this.createWrappers(wrappersToCreate),
			this.updateWrappers(wrappersToUpdate),
			this.deleteWrappers(wrappersToDelete, programItem),
		])

		console.log("====== syncFutureRecurringEvents end =======")
		this.logWrapper(baseWrapper, "base: ")
		this.logWrapperArray(wrappersToUpdate, "update: ")
		this.logWrapperArray(wrappersToCreate, "create: ")
		this.logWrapperArray(wrappersToDelete, "delete: ")

	}

	async createWrappers(wrapperSeries){
		console.log("createWrappers: wrapperSeries = ", wrapperSeries)
		if(!wrapperSeries || wrapperSeries.length === 0){
			return Promise.resolve()
		}
		let sessionItemArray = []
		let promiseArray = []
		wrapperSeries.forEach(({sessionItem, sessionType}) => {
			if(sessionItem) {sessionItemArray.push(sessionItem)}
			// sessionType is now global so we don't touch it
			// if(sessionType) {promiseArray.push(this.dataManager.createEventType(sessionType))}
		})

		if(sessionItemArray.length > 0){
			promiseArray.push(this.dataManager.apiPostScheduledEventCreateMultiple(sessionItemArray))
		}
		await Promise.all(promiseArray)
	}

	async updateWrappers(wrapperSeries){
		console.log("updateWrappers: wrapperSeries = ", wrapperSeries)
		if(!wrapperSeries || wrapperSeries.length === 0){
			return Promise.resolve()
		}
		let sessionItemArray = []
		let promiseArray = []
		wrapperSeries.forEach(({sessionItem, sessionType}) => {
			if(sessionItem) {sessionItemArray.push(sessionItem)}
			// sessionType is now global so we don't touch it
			// if(sessionType) {promiseArray.push(this.updateEventTypeSecure(sessionType))}
		})

		if(sessionItemArray.length > 0){
			promiseArray.push(this.dataManager.apiPostScheduledEventUpdateMultiple(sessionItemArray))
		}
		await Promise.all(promiseArray)
	}

	async updateEventTypeSecure(sessionType){
		// in some rare cases it's possible that SessionType was not created by this point
		// for example during "delete" flow we don't ensure existence of sessionType in the db
		// this is an additional guard
		try{
			await this.dataManager.updateEventType(sessionType)
		} catch(error){
			console.warn(error)
		}
	}

	async deleteWrappers(wrapperSeries, programItem, keepBaseStep = false){
		console.log("deleteWrappers: wrapperSeries = ", wrapperSeries, ", programItem = ", programItem)
		if(!wrapperSeries || wrapperSeries.length === 0){
			return Promise.resolve()
		}
		let sessionItemArray = []
		let promiseArray = []
		wrapperSeries.forEach(({programStep, sessionItem, sessionType}) => {
			programStep.hasMoreRecurringEvents = false
			if(sessionItem){sessionItemArray.push(sessionItem)}
			// sessionType is now global so we don't touch it
			// if(sessionType){promiseArray.push(this.dataManager.deleteEventType(sessionType))}
		})

		this.deleteProgramSteps(wrapperSeries, programItem, keepBaseStep)

		if(sessionItemArray.length > 0){
			promiseArray.push(this.dataManager.apiPostScheduledEventDeleteMultiple(sessionItemArray))
		}
		await Promise.all(promiseArray)
	}

	setupInstanceIndexes(baseSessionItem, recurringInstances){
		for(let i = 0; i < recurringInstances.length;++i){
			let calendarEvent = recurringInstances[i]
			calendarEvent.recurrenceIndex = baseSessionItem.recurrenceIndex + i
		}
	}

	createNewSectionWithSessionFromCalendarEvent(baseWrapper, calendarEvent, programItem){
		console.log("createFutureRecurringEventsIfNeeded: baseWrapper = ", baseWrapper, ", calendarEvent = ", calendarEvent)
		let sessionWrapper = this.createNewSessionStepFromCalendarEvent(baseWrapper, calendarEvent, programItem)
		let recurrenceIndex = calendarEvent.recurrenceIndex - baseWrapper.sessionItem.recurrenceIndex
		let section = programItem.findParentStepForStep(baseWrapper.programStep)
		let newSectionName = newNumberedName(section.name, recurrenceIndex)
		console.log("createFutureRecurringEventsIfNeeded: newSectionName = ", newSectionName, ", section.name = ", section.name, ", section = ", section)
		let newSection = new SectionProgramStep(newSectionName, "", [sessionWrapper.programStep])
		newSection.hasMoreRecurringEvents = calendarEvent.hasMoreRecurringEvents
		console.log("createFutureRecurringEventsIfNeeded: sessionWrapper = ", sessionWrapper, ", newSection = ", newSection)

		programItem.addSection(newSection)

		return sessionWrapper
	}


	createNewSessionStepFromCalendarEvent(baseWrapper, calendarEvent, programItem){
		let programWrapper = classToClass(baseWrapper)

		let newStep = programWrapper.programStep
		newStep.resetId()

		let newSessionItem = programWrapper.sessionItem
		newSessionItem.id = this.dataManager.generateId()
		newSessionItem.setupCancelAndRescheduleLinks()
		newSessionItem.remindersResetScheduledIds()

		newStep.schemaVersion = 2
		newStep.sessionTypeId = programWrapper.sessionType.id
		newStep.sessionItemId = programWrapper.sessionItem.id

		// sessionType is now a single global object which is the same for each sessions
		// so we don't create new instances nor we delete the old ones
		// let newSessionType = programWrapper.sessionType
		// newSessionType.id = this.dataManager.generateId()

		return this.fillWrapperFromCalendarEvent(baseWrapper, programWrapper, calendarEvent, programItem)
	}



	fillWrapperFromCalendarEvent(baseWrapper, programWrapper, calendarEvent, programItem){
		let newStep = programWrapper.programStep
		let newSessionItem = programWrapper.sessionItem
		// let newSessionType = programWrapper.sessionType

		newStep.hasMoreRecurringEvents = calendarEvent.hasMoreRecurringEvents
		if(newSessionItem){
			newSessionItem.hasMoreRecurringEvents = calendarEvent.hasMoreRecurringEvents
			newSessionItem.recurrenceIndex = calendarEvent.recurrenceIndex
			this.syncRecurrencePattern(baseWrapper, programWrapper, calendarEvent)
			newSessionItem.isMasterEvent = null
			newSessionItem.startTime = calendarEvent.startTime
			this.fillCalendarIdsFromCalendarEvent(newSessionItem, calendarEvent)

			newSessionItem.programId = programItem.id
			newSessionItem.stepId = newStep.id
			newSessionItem.removeOwnersAll()
			newSessionItem.addOwner([newStep.id, programItem.id, programWrapper.sessionItem.coachId])
			this.fillStepFromEventInstance(newStep, newSessionItem)
		}

		// if(newSessionType){
		// 	newSessionType.removeOwnersAll()
		// 	newSessionType.addOwner([newStep.id, programItem.id, programWrapper.sessionItem.coachId])
		// }

		return programWrapper
	}

	syncRecurrencePattern(baseWrapper, programWrapper){
		let baseSessionItem = baseWrapper.sessionItem
		let sessionItem = programWrapper.sessionItem
		let programStep = programWrapper.programStep

		let isCountType = sessionItem && sessionItem.recurrence && sessionItem.recurrence.isRangeByCount()
		let baseIsCountType = baseSessionItem && baseSessionItem.recurrence && baseSessionItem.recurrence.isRangeByCount()
		let baseIsMaster = baseSessionItem && (baseSessionItem.isMasterEvent || baseSessionItem.recurrenceIndex === 1)
		let stepIsCountType = programStep && programStep.recurrence && programStep.recurrence.isRangeByCount()

		if(isCountType && baseIsCountType && stepIsCountType){
			let masterBaseNumber
			if(baseIsMaster){
				masterBaseNumber = baseSessionItem.recurrence.range.numberOfOccurrences
			} else {
				masterBaseNumber = baseSessionItem.recurrence.range.masterNumberOfOccurrences
			}
			let baseNumber = masterBaseNumber || baseSessionItem.recurrence.range.numberOfOccurrences
			let newNumber = baseNumber - sessionItem.recurrenceIndex + 1

			sessionItem.recurrence.range.numberOfOccurrences = newNumber
			programStep.recurrence.range.numberOfOccurrences = newNumber

			if(masterBaseNumber > 0){
				sessionItem.recurrence.range.masterNumberOfOccurrences = masterBaseNumber
				programStep.recurrence.range.masterNumberOfOccurrences = masterBaseNumber
			}

			console.log("syncRecurrencePattern: baseNumber = ", baseNumber, ", sessionItem.recurrenceIndex = ", sessionItem.recurrenceIndex, ", newNumber = ", sessionItem.recurrence.range.numberOfOccurrences)
		}
	}

	createGetCalendarEventRequest(sessionItem, limit = 5, startTime, endTime){
		let coachId = this.dataManager.ownerId
		let request = new GetCalendarEventRequest(coachId, sessionItem.googleEventId, sessionItem.outlook365EventId, sessionItem.iCloudEventId)
		request.calendarId = sessionItem.calendarId || null
		request.startTime = startTime || sessionItem.startTime
		request.endTime = endTime || dayjs(request.startTime).add(1, "years").valueOf()
		request.limit = limit
		console.log("createGetCalendarEventRequest = ", request)
		return request
	}

	async getEventInstances(sessionItem, itemsToShow = this.recurringEventsToShow, startTime, endTime){
		console.log("getEventInstances: sessionItem = ", sessionItem)
		sessionItem = sessionItem.createRecurringMasterEvent()
		let limit = itemsToShow + 1
		let request = this.createGetCalendarEventRequest(sessionItem, limit, startTime, endTime)
		let calendarEventResponse = await this.dataManager.apiGetCalendarEventRecurringInstances(request) || new CalendarEventResponse()
		this.postProcessCalendarEventResponse(sessionItem, calendarEventResponse, itemsToShow)
		return calendarEventResponse
	}

	async updateRecurringSession(baseWrapper, programItem, changeType = RecurringChangeType.ONLY_THIS, clientProfile, shouldNotifyClient = false){
		console.log("syncRecurringSession.updateRecurringSession: start, changeType =  ", changeType)

		if(baseWrapper.sessionItem && baseWrapper.sessionItem.isRecurring()){
			switch (changeType) {
				case RecurringChangeType.ONLY_THIS:
					return await this.updateRecurringSessionThis(baseWrapper, programItem, shouldNotifyClient)
				case RecurringChangeType.ALL:
					return await this.updateRecurringSessionAll(baseWrapper, programItem, shouldNotifyClient)
				case RecurringChangeType.THIS_AND_FUTURE:
					return await this.updateRecurringSessionThisAndFuture(baseWrapper, programItem, shouldNotifyClient)
			}
		}
	}

	async updateRecurringSessionThis(baseWrapper, programItem, shouldNotifyClient){
		await this.updateOneTimeSession(baseWrapper, programItem, shouldNotifyClient)
	}

	async updateRecurringSessionThisAndFuture(sessionWrapper, programItem, shouldNotifyClient){


		let isFirst = sessionWrapper.sessionItem.isFirstSessionInSeries()
		console.log("updateRecurringSessionThisAndFuture: isFirstSessionInSeries = ", isFirst)
		if(isFirst){
			return await this.updateRecurringSessionAll(sessionWrapper, programItem, shouldNotifyClient)
		}
		console.log("updateRecurringSessionThisAndFuture: sessionWrapper = ", sessionWrapper)
		let startStepId = sessionWrapper.programStep.id

		let baseWrapper = sessionWrapper

		let sessionWrapperSeries = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem, {startStepId})
		this.logWrapperArray(sessionWrapperSeries, "debug1: findAllRecurringSessionsInSeries: ")



		let recurringEventsToShow = await this.recurringEventsToShowFromNextRecurringSession(baseWrapper, programItem)


		let calendarEventResponse = await this.recurringEventUpdateFuture(baseWrapper.sessionItem, recurringEventsToShow, shouldNotifyClient)

		baseWrapper.programStep.recurrence = classToClass(calendarEventResponse.masterEventNew.recurrence)
		baseWrapper.fillSessionFromStep()
		this.syncSeriesWithSplitResponse(sessionWrapperSeries, calendarEventResponse, calendarEventResponse.masterEventNew) //also sets the correct sessionItem.startTime
		this.logWrapperArray(sessionWrapperSeries, "debug1: after syncSeriesWithSplitResponse: ")

		let newStartTime = calendarEventResponse.masterEventNew.startTime
		baseWrapper.sessionItem.recurrenceIndex = 1
		baseWrapper.sessionItem.startTime = newStartTime
		baseWrapper.sessionItem.startTimeRecurringMaster = newStartTime

		await this.syncFutureRecurringEvents(baseWrapper, sessionWrapperSeries, programItem, calendarEventResponse.recurringInstances)

		await this.syncPreviousEvents(baseWrapper, programItem, calendarEventResponse, true)

	}

	async recurringEventsToShowFromNextRecurringSession(baseWrapper, programItem){
		this.logWrapper(baseWrapper, "recurringEventsToShowFromNextRecurringSession: ")
		let recurringEventsToShow = this.recurringEventsToShow
		let programStep = programItem.findNextRecurringSession()
		if(programStep){

			console.log("recurringEventsToShowFromNextRecurringSession: nextStep = ", new Date(programStep.confirmedTime) + ", baseStep = ", new Date(baseWrapper.programStep.confirmedTime), )

			let options = {
				includeAllSeries: true,
				startStepId: programStep.id,
				endStepId: baseWrapper.programStep.id,
				skipCancelled: true
			}
			let distance = 0;
			if(options.startStepId !== options.endStepId){
				distance = this.findRecurringStepsFromSeries(null, programItem, options).length
			}
			let newNumber = recurringEventsToShow - distance
			console.log("recurringEventsToShowFromNextRecurringSession: next session to base session distance = ", distance, ", newNumber = ", newNumber)
			recurringEventsToShow = Math.min(this.recurringEventsToShow, Math.max(1, newNumber))
		}
		console.log("recurringEventsToShow = ", recurringEventsToShow)

		return recurringEventsToShow

	}

	async syncPreviousEvents(baseWrapper, programItem, calendarEventResponse = null, disableEditing = null, shouldIncludeEndStepId: false){
		console.log("syncPreviousEvents start: disableEditing = ", disableEditing)
		this.logWrapper(baseWrapper, "syncPreviousEvents start: ")
		let startStepId = baseWrapper.programStep.id

		let optionsBefore = {
			endStepId: startStepId,
			shouldCountFromEnd: true,
			limit: 5,
			skipLoadingSessionType: true,
			includeAllSeries: true,
			shouldIncludeEndStepId: shouldIncludeEndStepId
		}
		let sessionWrapperSeriesBefore = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem, optionsBefore)

		// TODO need to sync previous ids for instances as google changes them after updating master event
		// but it's possible that each event refers to a unique sequence so this must be smarter than below
		// otherwise it can completely corrupt the calendar ids
		// let firstWrapper = sessionWrapperSeriesBefore[0]
		// if(firstWrapper && firstWrapper.sessionItem){
		// 	let calendarResponse = await this.getEventInstances(firstWrapper.sessionItem, sessionWrapperSeriesBefore.length)
		// 	this.syncSeriesWithSplitResponse(sessionWrapperSeriesBefore, calendarResponse)
		// }

		this.logWrapperArray(sessionWrapperSeriesBefore, "debug1: findAllRecurringSessionsInSeries sessions before this: ")

		let index = 0
		for (let wrapper of sessionWrapperSeriesBefore){
			wrapper.saveFormerSession()
			if(calendarEventResponse && calendarEventResponse.masterEventOld){
				wrapper.programStep.recurrence = classToClass(calendarEventResponse.masterEventOld.recurrence)
				console.log("syncPreviousEvents: wrapper.programStep.recurrence = ", wrapper.programStep.recurrence)
				if(wrapper.programStep.recurrence.isRangeByCount()){
					let newNumber = Math.max(1, wrapper.programStep.recurrence.range.numberOfOccurrences - index)
					wrapper.programStep.recurrence.range.numberOfOccurrences = newNumber
					if(wrapper.programItem && wrapper.programItem.recurrence){
						wrapper.programItem.recurrence.range.numberOfOccurrences = newNumber
					}
				}
			}
			wrapper.programStep.hasMoreRecurringEvents = false
			wrapper.programStep.isSplitEventPast = disableEditing
			if(wrapper.sessionItem){
				wrapper.sessionItem.hasMoreRecurringEvents = false
			}
			wrapper.fillSessionFromStep()

			index++
		}
		if(sessionWrapperSeriesBefore.length > 0){
			await this.updateWrappers(sessionWrapperSeriesBefore)
		}
	}

	syncSeriesWithSplitResponse(sessionWrapperSeries, calendarEventResponse, masterSessionItem){
		console.log("syncSeriesWithSplitResponse: masterSessionItem = ", masterSessionItem)
		let len = Math.min(sessionWrapperSeries.length, calendarEventResponse.recurringInstances.length)
		for(let i = 0; i < len; ++i){
			let {programStep, sessionItem, sessionType} = sessionWrapperSeries[i]
			let calendarEvent = calendarEventResponse.recurringInstances[i]
			console.log("debug2: syncSeriesWithSplitResponse before: sessionItem  = ", classToClass(sessionItem), ", calendarEvent = ", classToClass(calendarEvent))
			if(!sessionItem){
				console.warn("syncSeriesWithSplitResponse: sessionItem is null")
				continue
			}

			if(masterSessionItem){
				sessionItem.confirmationStatus = SessionConfirmationStatus.CONFIRMED
				sessionItem.pendingBookingKey = null
				sessionItem.pendingStatusExpirationTs = 0

				sessionItem.startTimeRecurringMaster = masterSessionItem.startTime
			}
			this.fillCalendarIdsFromCalendarEvent(sessionItem, calendarEvent)
			console.log("debug2: syncSeriesWithSplitResponse after: sessionItem  = ", classToClass(sessionItem), ", calendarEvent = ", classToClass(calendarEvent))
		}
	}


	async updateRecurringSessionAll(sessionWrapper, programItem, shouldNotifyClient){
		console.log("updateRecurringSessionAll: sessionWrapper = ", sessionWrapper)
		let sessionWrapperSeries = await this.findAllRecurringSessionsInSeries(sessionWrapper, programItem)

		let masterEvent = sessionWrapper.sessionItem.createRecurringMasterEvent()
		masterEvent.startTime = sessionWrapper.sessionItem.startTime
		masterEvent.notifyClient = shouldNotifyClient
		await this.dataManager.apiPostCalendarEventUpdate(masterEvent)
		let response = await this.getEventInstances(masterEvent)
		this.syncSeriesWithSplitResponse(sessionWrapperSeries, response, response.event)

		await this.syncFutureRecurringEvents(sessionWrapper, sessionWrapperSeries, programItem, response.recurringInstances)
	}



	async deleteRecurringSession(baseWrapper, programItem, changeType = RecurringChangeType.ONLY_THIS, shouldNotifyClient){
		console.log("syncRecurringSession.deleteRecurringSession: start ")
		switch (changeType) {
			case RecurringChangeType.ONLY_THIS:
				return await this.deleteRecurringSessionThis(baseWrapper, programItem, shouldNotifyClient)
			case RecurringChangeType.ALL:
				return await this.deleteRecurringSessionAll(baseWrapper, programItem, false, shouldNotifyClient)
			case RecurringChangeType.THIS_AND_FUTURE:
				return await this.deleteRecurringSessionThisAndFuture(baseWrapper, programItem, false, shouldNotifyClient)

		}
	}

	async deleteRecurringSessionThis(baseWrapper, programItem, shouldNotifyClient){
		await this.deleteOneTimeSession(baseWrapper, programItem, shouldNotifyClient)
	}

	async deleteRecurringSessionThisAndFuture(baseWrapper, programItem, keepBaseStep = false, shouldNotifyClient = false){
		console.log("deleteRecurringSessionThisAndFuture: keepBaseStep = ", keepBaseStep, ", shouldNotifyClient = ", shouldNotifyClient)
		if(baseWrapper.sessionItem && baseWrapper.sessionItem.isFirstSessionInSeries()){
			return await this.deleteRecurringSessionAll(baseWrapper, programItem, keepBaseStep, shouldNotifyClient)
		}

		let startStepId = baseWrapper.programStep.id
		let sessionWrapperSeries = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem, {startStepId})

		let sessionItem = classToClass(baseWrapper.sessionItem)
		if(sessionItem){
			sessionItem.notifyClient = shouldNotifyClient
		}

		console.log("deleteRecurringSessionThisAndFuture: sessionItem = ", sessionItem)
		let [calendarEventResponse] = await Promise.all([
			sessionItem ? this.dataManager.apiPostCalendarEventDeleteFuture(sessionItem) : Promise.resolve(),
			this.deleteWrappers(sessionWrapperSeries, programItem, keepBaseStep),
		])

		await this.syncPreviousEvents(baseWrapper, programItem, calendarEventResponse, false)

	}


	async deleteRecurringSessionAll(baseWrapper, programItem, keepBaseStep, shouldNotifyClient){
		console.log("deleteRecurringSessionAll")
		let sessionWrapperSeries = await this.findAllRecurringSessionsInSeries(baseWrapper, programItem)
		if(sessionWrapperSeries.length === 0){
			sessionWrapperSeries.push(baseWrapper)
		}

		baseWrapper = sessionWrapperSeries[0]
		let masterEvent
		if(baseWrapper.sessionItem){
			masterEvent = baseWrapper.sessionItem.createRecurringMasterEvent()
			masterEvent.notifyClient = shouldNotifyClient
		}

		await Promise.all([
			masterEvent ? this.dataManager.apiPostCalendarEventDelete(masterEvent) : Promise.resolve(),
			this.deleteWrappers(sessionWrapperSeries, programItem, keepBaseStep)
		])

		await this.syncPreviousEvents(baseWrapper, programItem)

	}

	deleteProgramSteps(sessionWrapperSeries, programItem, keepBaseStep = false){
		console.log("deleteProgramSteps: keepBaseStep = ", keepBaseStep)
		let i = 0
		sessionWrapperSeries.forEach(wrapper => {
			let step = wrapper.programStep
			programItem.findStep(stepLocal => stepLocal.id === step.id, (steps, index, parentStep) => {
				let shouldKeepStep = keepBaseStep && i === 0 && steps.length === 1
				console.log("deleteProgramSteps: shouldKeepStep = ", shouldKeepStep, ", step = ", steps[index])
				if(!shouldKeepStep){
					steps.splice(index, 1)
				}
				console.log("deleteProgramSteps: steps.length = ", steps.length)
				if(steps.length === 0 && i > 0){
					programItem.deleteStepById(parentStep.id)
				}
			})
			i++
		})
	}


	async findAllRecurringSessionsInSeries(sessionWrapper, programItem, options = {}){

		let recurringSteps = []
		if(sessionWrapper.sessionItem){
			recurringSteps = this.findRecurringStepsFromSeries(sessionWrapper.sessionItem, programItem, options)
		}
		// else {
		// 	recurringSteps = [classToClass(sessionWrapper)]
		// }



		let res = await this.loadWrappersForSteps(recurringSteps, sessionWrapper.sessionType, options)

		if(res.length === 0){
			console.warn("findAllRecurringSessionsInSeries: res.length is 0. This shouldn't happen. probably...")
		}
		console.log("findAllRecurringSessionsInSeries: res = ", res)

		return res
	}

	async loadWrappersForSteps(programSteps, sessionType, options = {}){
		let skipLoadingSessionType = options.skipLoadingSessionType || false
		let skipLoadingSessionItem = options.skipLoadingSessionItem || false

		let res = []
		console.log("loadWrappersForSteps: programSteps = ", programSteps)
		for (let step of programSteps){
			console.log("loadWrappersForSteps: step = ", step)

			let skipLoad = step.sessionItemId == null || skipLoadingSessionItem
			let [sessionItem] = await Promise.all([
				skipLoad ? Promise.resolve() : this.dataManager.getScheduledEventItem(step.sessionItemId)
			])

			// let len = Math.max(1, sessionTypeArray.length)
			console.log("loadWrappersForSteps: sessionType = ", sessionType)
			console.log("loadWrappersForSteps: sessionItem = ", sessionItem)
			res.push(new ProgramSessionWrapper(step, sessionItem, sessionType))
			// for(let i = 0; i < len; ++i){
			// 	res.push(new ProgramSessionWrapper(step, sessionItem, sessionTypeArray[i]))
			// }
		}
		return res

	}


	fillCalendarIdsFromCalendarEvent(sessionItem, calendarEvent) {
		sessionItem.googleEventId = calendarEvent.googleEventId
		sessionItem.googleMasterEventId = calendarEvent.googleMasterEventId
		sessionItem.outlook365EventId = calendarEvent.outlook365EventId
		sessionItem.outlook365MasterEventId = calendarEvent.outlook365MasterEventId
		sessionItem.iCloudEventId = calendarEvent.iCloudEventId
		sessionItem.iCloudMasterEventId = calendarEvent.iCloudMasterEventId

	}

	findRecurringStepsFromSeries(sessionItem, programItem, options = {}) {
		let index = 0
		let recurringSteps = programItem.findStepAll(step => {
			index++
			if(!step.isTypeCall() || !step.isRecurring){
				return false
			}
			if(options.skipCancelled && step.isSessionCancelled()){
				return false
			}
			if(options.startTime && step.confirmedTime && step.confirmedTime < options.startTime){
				return false
			}
			if(options.includeAllSeries || step.recurMasterId === sessionItem.getQwoachMasterEventId()){
				return true
			}
			return false

		})
		console.log("findRecurringStepsFromSeries: recurringSteps = ", recurringSteps)
		let startStepId = options.startStepId
		let endStepId = options.endStepId
		let limit = options.limit
		let shouldCountFromEnd = options.shouldCountFromEnd
		let shouldIncludeEndStepId = options.shouldIncludeEndStepId

		let isEndIdFound = false
		index = 0
		let canAddStep = startStepId == null
		recurringSteps = recurringSteps.filter(step => {
			if(step.id === startStepId){
				if(!isEndIdFound){
					canAddStep = true
				} else {
					console.warn("findRecurringStepsFromSeries: filter endId was found before startId")
				}
			}
			if(step.id === endStepId){
				isEndIdFound = true
				canAddStep = false
			}
			let canAddLocal = canAddStep || (step.id === endStepId && (shouldIncludeEndStepId || endStepId === startStepId))

			console.log("recurringSteps.filter[", index,"] = ", canAddLocal, ", step.confirmedTime = ", new Date(step.confirmedTime))
			index++
			return canAddLocal
		})
		if(limit){
			if(shouldCountFromEnd){
				recurringSteps = recurringSteps.slice(Math.max(0, recurringSteps.length - limit))
			} else {
				recurringSteps = recurringSteps.slice(0, Math.min(limit, recurringSteps.length))
			}
		}
		console.log("findAllRecurringSessionsInSeries: after filter startStepId = ", startStepId, ", recurringSteps = ", recurringSteps)

		return recurringSteps;
	}

	async recurringEventUpdateFuture(sessionItem, recurringEventsToShow = this.recurringEventsToShow, shouldNotifyClient){
		let limit = recurringEventsToShow + 1
		sessionItem = classToClass(sessionItem)
		sessionItem.notifyClient = shouldNotifyClient
		let request = {
			sessionItem,
			endTime: dayjs(sessionItem.startTime).add(1, "years").valueOf(),
			limit
		}
		let calendarEventResponse = await this.dataManager.apiPostCalendarEventUpdateFuture(request) || new CalendarEventResponse()
		sessionItem.recurrenceIndex = 1
		this.postProcessCalendarEventResponse(sessionItem, calendarEventResponse, recurringEventsToShow)

		return calendarEventResponse
	}
	postProcessCalendarEventResponse(sessionItem, calendarEventResponse, itemsToShow = this.recurringEventsToShow){
		this.calculateHasMoreItems(calendarEventResponse.recurringInstances, itemsToShow)
		this.setupInstanceIndexes(sessionItem, calendarEventResponse.recurringInstances)
		console.log("postProcessCalendarEventResponse: calendarEventResponse = ", calendarEventResponse)
	}

	calculateHasMoreItems(recurringInstances, itemsToShow = this.recurringEventsToShow) {
		// we request more items than we show to see if there are more items to show in future
		// after the check, we need to remove the redundant items if any
		let hasMoreRecurringEvents = false
		while(recurringInstances.length > itemsToShow){
			recurringInstances.pop()
			hasMoreRecurringEvents = true
		}
		for(let calendarEvent of recurringInstances){
			calendarEvent.hasMoreRecurringEvents = hasMoreRecurringEvents
		}
		return hasMoreRecurringEvents;
	}

}
